import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, FileText } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { useTranslation } from 'react-i18next';

/**
 * The customer's own fiscal data, and whether we invoice them automatically.
 *
 * Two things are deliberate here. The régimen and the uso are chosen from the
 * SAT's own lists rather than typed, because they are assertions to the tax
 * authority and a typo in one is a comprobante the PAC refuses. And the data is
 * asked for *before* anything is stamped: the ERP will not rewrite the fiscal
 * details of a client it already holds, so a guess made now would be permanent.
 */

interface Catalogue {
  code: string;
  label: string;
}

interface FiscalData {
  taxId: string | null;
  legalName: string | null;
  taxRegime: string | null;
  fiscalPostalCode: string | null;
  cfdiUse: string | null;
  complete: boolean;
  autoInvoice: boolean;
  /** Invoicing arranged with us, ahead of payment. Not theirs to switch off. */
  arrangedByUs: boolean;
}

export default function FiscalDataForm() {
  const { t } = useTranslation();
  const [data, setData] = useState<FiscalData | null>(null);
  const [regimes, setRegimes] = useState<Catalogue[]>([]);
  const [uses, setUses] = useState<Catalogue[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [taxId, setTaxId] = useState('');
  const [legalName, setLegalName] = useState('');
  const [taxRegime, setTaxRegime] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [cfdiUse, setCfdiUse] = useState('G03');
  const [autoInvoice, setAutoInvoice] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [fiscal, catalogues] = await Promise.all([
          apiFetch<FiscalData>('/customer/fiscal'),
          apiFetch<{ taxRegimes: Catalogue[]; cfdiUses: Catalogue[] }>('/customer/fiscal/catalogues'),
        ]);
        const current = fiscal.data as FiscalData;
        setData(current);
        setTaxId(current.taxId || '');
        setLegalName(current.legalName || '');
        setTaxRegime(current.taxRegime || '');
        setPostalCode(current.fiscalPostalCode || '');
        setCfdiUse(current.cfdiUse || 'G03');
        setAutoInvoice(current.autoInvoice);
        setRegimes((catalogues.data as any)?.taxRegimes || []);
        setUses((catalogues.data as any)?.cfdiUses || []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await apiFetch<{ backfilled: number }>('/customer/fiscal', {
        method: 'PUT',
        body: JSON.stringify({
          taxId,
          legalName,
          taxRegime,
          fiscalPostalCode: postalCode,
          cfdiUse: cfdiUse || undefined,
          autoInvoice,
        }),
      });
      const backfilled = (result.data as any)?.backfilled ?? 0;
      setSuccess(
        backfilled > 0
          ? `Datos guardados. Estamos generando ${backfilled} factura(s) de tus compras recientes.`
          : 'Datos fiscales guardados.'
      );
      setData((d) => (d ? { ...d, complete: true, autoInvoice } : d));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <form onSubmit={save} className="bg-white rounded-lg shadow p-6 space-y-6">
      <div className="flex items-start gap-3">
        <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
        <div>
          <h2 className="font-semibold text-lg">{t('fiscal.formTitle')}</h2>
          <p className="text-sm text-gray-500">
            Captúralos tal como aparecen en tu Constancia de Situación Fiscal. El SAT valida el RFC
            contra la razón social, el régimen y el código postal: si alguno no coincide, el CFDI se
            rechaza.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-700 text-sm">
          <CheckCircle className="h-4 w-4" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">RFC</label>
          <input
            className="input w-full uppercase"
            value={taxId}
            onChange={(e) => setTaxId(e.target.value.toUpperCase())}
            placeholder="XAXX010101000"
            maxLength={13}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('fiscal.legalNameLbl')}</label>
          <input
            className="input w-full"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            placeholder="Como aparece en tu constancia, sin el régimen"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('fiscal.regimeLbl')}</label>
          <select
            className="input w-full"
            value={taxRegime}
            onChange={(e) => setTaxRegime(e.target.value)}
            required
          >
            <option value="">{t('fiscal.choose')}</option>
            {regimes.map((r) => (
              <option key={r.code} value={r.code}>
                {r.code} — {r.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('fiscal.postalLbl')}
          </label>
          <input
            className="input w-full"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, ''))}
            placeholder="64000"
            maxLength={5}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {t('fiscal.postalHelp')}
          </p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('fiscal.cfdiUseLbl')}</label>
          <select className="input w-full" value={cfdiUse} onChange={(e) => setCfdiUse(e.target.value)}>
            {uses.map((u) => (
              <option key={u.code} value={u.code}>
                {u.code} — {u.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4">
        {data?.arrangedByUs ? (
          <p className="text-sm text-gray-600">
            Tus pedidos se facturan al emitirse, antes del pago, según lo acordado con nosotros.
            Si necesitas cambiarlo, escríbenos.
          </p>
        ) : (
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={autoInvoice}
              onChange={(e) => setAutoInvoice(e.target.checked)}
            />
            <span className="text-sm text-gray-700">
              <span className="font-medium">{t('fiscal.autoInvoice')}</span> Emitimos el CFDI de cada
              compra en cuanto quede pagada por completo, sin que tengas que pedirlo.
            </span>
          </label>
        )}
      </div>

      <div className="flex justify-end">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}
